import { Alert, Anchor, Button, PinInput, Stack, Text } from '@mantine/core';
import { IconMailCheck } from '@tabler/icons-react';
import { INTAKE_SIGNIN_RETURN_PATH } from '../intakeResumeState';
import type { IntakeAnswers, IntakeAction } from '../types';

interface StepVerifyEmailProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  verificationError?: string | null;
  resendLoading?: boolean;
  onResend?: () => void | Promise<void>;
}

export function StepVerifyEmail({
  answers,
  dispatch,
  verificationError,
  resendLoading = false,
  onResend,
}: StepVerifyEmailProps): JSX.Element {
  if (answers.existingClient) {
    return (
      <Stack align="center" gap="lg" py="md">
        <Alert color="blue" variant="light" title="You're already a client" maw={440}>
          You&apos;re already a client in our system. Please log in to your client portal to submit
          new service requests.
        </Alert>
        <Button component="a" href={INTAKE_SIGNIN_RETURN_PATH} color="gold" variant="filled">
          Go to sign in
        </Button>
      </Stack>
    );
  }

  return (
    <Stack align="center" gap="lg" py="md">
      <IconMailCheck size={48} stroke={1.4} color="var(--mantine-color-gold-6)" />
      <Text size="sm" c="dimmed" ta="center" maw={400}>
        We sent a verification code to <b>{answers.email || 'your email'}</b>.
        Please enter it below to continue.
      </Text>
      <PinInput
        length={6}
        type="number"
        size="lg"
        value={answers.emailVerificationCode}
        onChange={(value) => {
          dispatch({ type: 'SET_EMAIL_VERIFICATION_CODE', payload: value });
        }}
        oneTimeCode
        aria-label="Verification code"
      />
      {verificationError ? (
        <Text size="sm" c="red" ta="center" maw={400}>
          {verificationError}
        </Text>
      ) : null}
      <Stack align="center" gap={4}>
        <Text size="xs" c="dimmed" ta="center">
          Didn&apos;t receive a code? Check your spam folder or{' '}
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => void onResend?.()}
            disabled={resendLoading || !onResend}
          >
            resend the code
          </Anchor>
          .
        </Text>
        <Text size="xs" c="dimmed">
          Wrong email? Go back to the previous step to update it.
        </Text>
      </Stack>
    </Stack>
  );
}
